import { useState, useRef, useId } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useClerkApi } from '../hooks/useClerkApi';
import { useClickOutside, useEscapeKey } from '../hooks/useClickOutside';
import { SeasonList } from './SeasonList';
import { seasonQuery } from '../utils/season';
import { searchWith } from '../utils/leagueRoutes';
import { sharedApiBase } from '../utils/api';

export function SeasonDropdown({ leagueId, provider, activeSeason, shareToken = null }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { authFetch } = useClerkApi();
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const menuId = useId();
  useClickOutside(ref, open, () => setOpen(false));
  useEscapeKey(open, () => { setOpen(false); triggerRef.current?.focus(); });

  if (!provider) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        className="ledger-season-btn"
        aria-label={`Season: ${activeSeason ?? 'Live'}`}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '0 10px', borderRadius: '6px',
          background: 'var(--ledger-panel)', border: '1px solid var(--ledger-line-2)',
          cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'var(--ledger-ink)',
        }}
      >
        {activeSeason ?? 'Live'}
        <ChevronDown aria-hidden="true" style={{ width: '13px', height: '13px', color: 'var(--ledger-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }} />
      </button>

      {open && (
        <div id={menuId} style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 30,
          minWidth: '220px', padding: '10px',
          background: 'var(--ledger-panel)',
          border: '1px solid var(--ledger-line)', borderRadius: '6px',
          color: 'var(--ledger-ink)',
          boxShadow: 'var(--ledger-shadow)',
        }}>
          <SeasonList
              leagueId={leagueId}
              authFetch={shareToken ? fetch : authFetch}
              apiBaseUrl={shareToken ? sharedApiBase(shareToken) : undefined}
              readOnly={Boolean(shareToken)}
              activeSeason={activeSeason}
              onSelectSeason={(entry) => {
                const isLive = entry.status === 'live';
                if (!isLive && seasonQuery(entry.season) == null) return;
                navigate({ pathname: location.pathname, search: searchWith(location.search, { season: isLive ? null : entry.season, changes: null }) });
                setOpen(false);
              }}
            />
        </div>
      )}
    </div>
  );
}

export default SeasonDropdown;
